import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import { useQuery } from "@apollo/client/react";

import ActionModal from "../../../components/modals/action-modal";
import useMutation from "../../../hooks/useMutation";
import { CREATE_INVENTORY_TRANSACTION_MUTATION } from "../../../constants/graphql";
import { ASSETS_COLLECTION_RESTOCK_INVENTORY_QUERY } from "../../../constants/graphql/queries";
import { ASSET_REPLAY_LOGS_QUERY } from "../../../constants/graphql/queries";
import { useAssetsCollection } from "../../../contexts/assetsCollection";
import RackLocationsForm from "./RackLocationsForm";
import useNotification from "../../../hooks/notification";

export default function RackLocationsModal({ open, toggleModal, entity, addAction }) {
  const { setCollection, setLoadingMutation } = useAssetsCollection();
  const { notifySuccess, notifyError } = useNotification();
  const [createTransactionMutation] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION);
  const [assetData, setAssetData] = useState(null);

  const { data } = useQuery(ASSETS_COLLECTION_RESTOCK_INVENTORY_QUERY, {
    variables: {
      search: entity.assetId,
      projectId: entity.projectId,
      type: "Asset",
    },
    skip: !entity.assetId,
  });

  useEffect(() => {
    if (data && data.assetsCollection.collection.length) {
      const fetchedAsset = data.assetsCollection.collection[0];
      setAssetData(fetchedAsset);
    }
  }, [data]);

  const handleSave = values => {
    setLoadingMutation(true);

    const locations = values.rackLocations.map(location => ({
      sub_qty: location.sub_qty,
      allocated_quantity: location.allocated_quantity ?? 0,
      warehouse: {
        id: location.warehouse?.id,
        warehouse_id: location.warehouse?.warehouseId || location.warehouse?.warehouse_id,
        name: location.warehouse?.name,
      },
      warehouse_locations: location.warehouse_location ? [{
        id: location.warehouse_location?.id,
        location_id: location.warehouse_location?.locationId || location.warehouse_location?.location_id,
        location_name: location.warehouse_location?.locationName || location.warehouse_location?.location_name,
      }] : (location.warehouse_locations || []),
      ...(location.mobile_warehouse_location && {
        mobile_warehouse_location: {
          id: location.mobile_warehouse_location?.id,
          location_id: location.mobile_warehouse_location?.locationId || location.mobile_warehouse_location?.location_id,
          location_name: location.mobile_warehouse_location?.locationName || location.mobile_warehouse_location?.location_name,
        },
      }),
    }));

    const oldRackLocations =
      assetData?.rackLocations.map(location => ({
        warehouse: {
          id: location.warehouse?.id,
          warehouse_id: location.warehouse?.warehouseId || location.warehouse?.warehouse_id,
          name: location.warehouse?.name,
        },
        warehouse_locations: location.warehouse_locations || [],
        mobile_warehouse_location: location.mobile_warehouse_location ? {
          id: location.mobile_warehouse_location?.id,
          location_id: location.mobile_warehouse_location?.locationId || location.mobile_warehouse_location?.location_id,
          location_name: location.mobile_warehouse_location?.locationName || location.mobile_warehouse_location?.location_name,
        } : null,
        sub_qty: location.sub_qty,
        allocated_quantity: location.allocated_quantity ?? 0,
      })) || [];

    const transactionPayload = {
      code: entity.assetId || "",
      notes: values.notes || "",
      senderName: values.senderName || "",
      carrierName: values.carrierName || "",
      trackingNumber: values.trackingNumber || "",
      orderNumber: values.orderNumber || "",
      poNumber: values.poNumber || "",
      quantity: values.quantity || 0,
      rackLocations: locations,
      rackLocationsChanges: [],
      oldRackLocations,
      id: entity.id,
      actionSource: "transaction_modal",
    };

    const refetchQuery = {
      query: ASSET_REPLAY_LOGS_QUERY,
      variables: { assetId: entity.id },
    };

    createTransactionMutation({
      variables: transactionPayload,
      refetchQueries: [refetchQuery],
      onSuccess: () => {
        setLoadingMutation(false);
        notifySuccess("Asset successfully updated!");
        toggleModal();
        if (addAction) {
          addAction([
            {
              [entity.id]: {
                rackLocations: entity.rackLocations,
              },
            },
            { [entity.id]: values },
          ]);
        }
      },
      onFailure: error => {
        setLoadingMutation(false);
        notifyError(error?.submit || error);
      },
    });
  };

  return open ? (
    <ActionModal
      open
      title="Storage Location(s)"
      maxWidth={640}
      onClose={toggleModal}
      disableTransition
      disableBackdropClose={true}
    >
      <Box mt={1} />
      <RackLocationsForm
        asset={entity}
        rackLocations={entity.rackLocations || []}
        toggleModal={toggleModal}
        onSubmit={handleSave}
      />
    </ActionModal>
  ) : null;
}
